import { WORD_TOLERANCE } from '../../../../../constants/quizeWordTolerance';
import {
  AnswerHotSpotType,
  EnhancedHotSpotAnswer,
} from '../../../../../types/quiz';

type MinMaxType = {
  min: number;
  max: number;
};

type ToleranceHotspotType = {
  startOffset: MinMaxType;
  endOffset: MinMaxType;
};

type OverlapHotSpotType = {
  a: AnswerHotSpotType;
  b: AnswerHotSpotType;
  ab: Omit<EnhancedHotSpotAnswer, 'text'>;
};

type CorrectHotSpotType = EnhancedHotSpotAnswer & {
  id: number;
  tolerance: ToleranceHotspotType;
  type?: string;
};

type UserAnswersType = {
  endOffset?: number;
  startOffset?: number;
  nodes?: Text[];
  text: string;
};

type AnswerHotspotsType = {
  tolerance: ToleranceHotspotType;
  id: number;
  startOffset: number;
  endOffset: number;
  text: string;
};

type GroupingAnswersType = Record<
  'correctHotspots' | 'incorrectHotspots' | 'missedHotspots',
  AnswerHotSpotType[]
>;

/**
 *Number of valid words approved for highlighting.
 */
export const calculateTolerance = (
  allText: string,
  hotspotOffset: EnhancedHotSpotAnswer,
  tolerance: number,
): ToleranceHotspotType => {
  const el = document.createElement('div');
  el.innerHTML = allText;
  const strippedText = el.textContent || el.innerText || '';

  let offset = 0;
  const words = strippedText.split(' ').map((word) => {
    const info = {
      word,
      start: offset,
      end: (offset += word.length),
    };

    offset += 1;

    return info;
  });

  const startWordIndex = words.findIndex(
    (word) =>
      +hotspotOffset.startOffset >= word.start &&
      +hotspotOffset.startOffset <= word.end,
  );

  const endWordIndex = words.findIndex(
    (word) =>
      +hotspotOffset.endOffset >= word.start &&
      +hotspotOffset.endOffset <= word.end,
  );

  const startMin = words[startWordIndex - tolerance] || words[0];
  const startMax = words[startWordIndex + tolerance] || words[words.length - 1];
  const endMin = words[endWordIndex - tolerance] || words[0];
  const endMax = words[endWordIndex + tolerance] || words[words.length - 1];

  return {
    startOffset: {
      min: startMin.start,
      max: startMax.end,
    },
    endOffset: {
      min: endMin.start,
      max: endMax.end,
    },
  };
};

/**
 * Splits two hotspot data objects into 3 pieces of text, a, ab, and b, where ab is the overlapping text.
 */
export const calculateOverlap = (
  hs1: AnswerHotSpotType,
  hs2: CorrectHotSpotType,
): OverlapHotSpotType | undefined => {
  const first = hs1.startOffset < hs2.startOffset ? hs1 : hs2;
  const second = hs1.startOffset < hs2.startOffset ? hs2 : hs1;
  // Combined text string
  const combinedText =
    first.text.substring(0, second.startOffset - first.startOffset) +
    second.text;

  // No overlap
  if (first.endOffset < second.startOffset) return undefined;

  // If overlapping like a venn diagram
  const a = {
    ...first,
    startOffset: first.startOffset,
    endOffset: second.startOffset,
    text: first.text.substring(0, second.startOffset - first.startOffset),
  };
  if (first.endOffset < second.endOffset)
    return {
      a,
      ab: {
        startOffset: second.startOffset,
        endOffset: first.endOffset,
      },
      b: {
        ...second,
        startOffset: first.endOffset,
        endOffset: second.endOffset,
        text: combinedText.substring(first.endOffset - first.startOffset),
      },
    };

  // If second is inside of first
  return {
    a,
    ab: { startOffset: second.startOffset, endOffset: second.endOffset },
    b: {
      ...first,
      startOffset: second.endOffset,
      endOffset: first.endOffset,
      text: first.text.substring(
        second.endOffset - first.startOffset,
        first.endOffset - first.startOffset,
      ),
    },
  };
};

/**
 * Check if user selection overlaps with a hotspot and is within tolerance.
 */
const overlapsWithinTolerance = (
  user: UserAnswersType,
  hotspot: AnswerHotspotsType,
) => {
  const startOffset = user.startOffset ?? 0;
  const endOffset = user.endOffset ?? 0;
  const overlap = calculateOverlap(
    {
      startOffset,
      endOffset,
      text: user.text,
    },
    hotspot,
  );

  return (
    !!overlap &&
    startOffset <= hotspot.tolerance.startOffset.max &&
    endOffset >= hotspot.tolerance.endOffset.min
  );
};

/**
 * Grouping answers into the correct, missed and incorrect.
 */
export const groupingAnswers = (
  userAnswers: UserAnswersType[],
  answerHotspots: AnswerHotspotsType[],
): GroupingAnswersType =>
  userAnswers.reduce(
    (hotspots: GroupingAnswersType, answer) => {
      if (!answer.startOffset || !answer.endOffset) return hotspots;

      // Allow a single selection to match multiple hotspots
      const matchedHotspots = answerHotspots.filter((hotspot) =>
        overlapsWithinTolerance(answer, hotspot),
      );

      if (matchedHotspots.length > 0) {
        matchedHotspots.forEach((hotspot) => {
          hotspots.correctHotspots.push({
            id: hotspot.id,
            text: hotspot.text,
            startOffset: hotspot.startOffset,
            endOffset: hotspot.endOffset,
          });
        });
        return hotspots;
      }

      let partialCorrect = false;

      answerHotspots.forEach((hotspot) => {
        // Calculate overlap between selected hotspots and the correct hotspots
        const overlap =
          answer?.startOffset && answer?.endOffset
            ? calculateOverlap(
                {
                  startOffset: answer.startOffset,
                  endOffset: answer.endOffset,
                  text: answer.text,
                  type: 'incorrect',
                },
                { ...hotspot, type: 'answer' },
              )
            : undefined;

        if (overlap) {
          partialCorrect = true;

          // Add the overlapping area as correct
          hotspots.correctHotspots.push({
            ...overlap.ab,
            id: hotspot.id,
            text: hotspot.text,
          });

          [(overlap.a, overlap.b)].forEach((outer) => {
            if (
              outer.type === 'answer' &&
              outer.text.split(' ').length > WORD_TOLERANCE
            ) {
              hotspots.missedHotspots.push(outer);
            }
            if (outer.type === 'incorrect') {
              hotspots.incorrectHotspots.push(outer);
            }
          });
        }
      });

      // If there's no overlap, then just add the whole selection as incorrect
      if (!partialCorrect && answer?.startOffset && answer?.endOffset) {
        hotspots.incorrectHotspots.push({
          startOffset: answer.startOffset,
          text: answer.text,
          endOffset: answer.endOffset,
        });
      }

      return hotspots;
    },
    {
      correctHotspots: [],
      incorrectHotspots: [],
      missedHotspots: [],
    } as GroupingAnswersType,
  );
